import React, { useCallback, useEffect, useState } from "react";
import { Alert, Button, Table } from "react-bootstrap";
import { clearAllProductPriceAlerts, clearProductPriceAlert, fetchProductPriceAlerts } from "../api/api";

const hasSalePriceChange = (alert) => (
    Boolean(alert?.changes?.retail_price) || Boolean(alert?.changes?.wholesale_price)
);

const normalizeList = (data) => {
    const alerts = Array.isArray(data) ? data : (data?.results || []);
    return alerts.filter(hasSalePriceChange);
};

const formatRetail = (value) => {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue.toFixed(2) : "0.00";
};

const formatWholesale = (value) => {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue.toFixed(3) : "0.000";
};

const getPriceDirection = (change) => {
    const oldValue = Number(change?.old);
    const newValue = Number(change?.new);

    if(!Number.isFinite(oldValue) || !Number.isFinite(newValue) || oldValue === newValue) {
        return null;
    }

    return newValue > oldValue ? "up" : "down";
};

const PriceDirection = ({ change }) => {
    const direction = getPriceDirection(change);
    if (!direction) return null;

    const isIncrease = direction === "up";
    return (
        <span
            className={`ms-1 fw-bold ${isIncrease ? "text-danger" : "text-success"}`}
            title={isIncrease ? "Price increased" : "Price reduced"}
            aria-label={isIncrease ? "Price increased" : "Price reduced"}
        >
            {isIncrease ? "↑" : "↓"}
        </span>
    );
};

const PriceValue = ({ value, change, type = "retail" }) => (
    <span>
        {type === "wholesale" ? formatWholesale(value) : formatRetail(value)}
        <PriceDirection change={change} />
    </span>
);

const getAlertSummary = (alert) => {
    const base = alert.base_unit_snapshot || {};
    const highest = alert.highest_unit_snapshot || {};
    const highestText = highest.unit_name
        ? ` Highest unit ${highest.unit_name}: retail ${formatRetail(highest.retail_price)}, wholesale ${formatWholesale(highest.wholesale_price)}.`
        : "";

    return `${alert.product_name} price update. Base ${base.unit_abbreviation || base.unit_name || "Base Unit"}: 
    retail ${formatRetail(base.retail_price)}, wholesale ${formatWholesale(base.wholesale_price)}.${highestText}`;
};

const PriceAlertsBanner = ({ compact = false }) => {
    const [alerts, setAlerts] = useState([]);
    const [loading, setLoading] = useState(false);

    const loadAlerts = useCallback(async () => {
        try {
            setLoading(true);
            const data = await fetchProductPriceAlerts();
            setAlerts(normalizeList(data));
        } catch (error) {
            console.error("Error loading product price alerts:", error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadAlerts();
    }, [loadAlerts]);

    const handleClear = async (alertId) => {
        await clearProductPriceAlert(alertId);
        setAlerts((current) => current.filter((alert) => alert.id !== alertId));
    };

    const handleClearAll = async () => {
        await clearAllProductPriceAlerts();
        setAlerts([]);
    };

    if (loading || alerts.length === 0) {
        return null;
    }

    return (
        <Alert variant="warning" className="price-alert-banner text-start">
            <div className="d-flex justify-content-between align-items-center gap-3 mb-2">
                <strong>Product Price Changes</strong>
                <Button variant="outline-dark" size="sm" onClick={handleClearAll}>
                    Clear all viewed
                </Button>
            </div>
            {compact ? (
                <div className="d-grid gap-2">
                    {alerts.slice(0, 3).map((alert) => (
                        <div key={alert.id} className="d-flex justify-content-between gap-2 align-items-start">
                            <span>
                                {getAlertSummary(alert)}
                                <PriceDirection change={alert.changes?.retail_price} />
                                <PriceDirection change={alert.changes?.wholesale_price} />
                            </span>
                            <Button variant="outline-dark" size="sm" onClick={() => handleClear(alert.id)}>
                                Clear
                            </Button>
                        </div>
                    ))}
                    {alerts.length > 3 && <small>{alerts.length - 3} more price changes are waiting.</small>}
                </div>
            ) : (
                <Table responsive size="sm" className="mb-0">
                    <thead>
                        <tr>
                            <th>Product</th>
                            <th>Base unit update</th>
                            <th>Highest alternative unit</th>
                            <th>Changed</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {alerts.map((alert) => (
                            <tr key={alert.id}>
                                <td>{alert.product_name}</td>
                                <td>
                                    {alert.base_unit_snapshot?.unit_name || "Base Unit"}: retail{" "}
                                    <PriceValue
                                        value={alert.base_unit_snapshot?.retail_price}
                                        change={alert.changes?.retail_price}
                                    />
                                    , wholesale{" "}
                                    <PriceValue
                                        value={alert.base_unit_snapshot?.wholesale_price}
                                        change={alert.changes?.wholesale_price}
                                        type="wholesale"
                                    />
                                </td>
                                <td>
                                    {alert.highest_unit_snapshot?.unit_name ? (
                                        <>
                                            {alert.highest_unit_snapshot.unit_name}: retail{" "}
                                            <PriceValue
                                                value={alert.highest_unit_snapshot.retail_price}
                                                change={alert.changes?.retail_price}
                                            />
                                            , wholesale{" "}
                                            <PriceValue
                                                value={alert.highest_unit_snapshot.wholesale_price}
                                                change={alert.changes?.wholesale_price}
                                                type="wholesale"
                                            />
                                        </>
                                    ) : "No alternative units"}
                                </td>
                                <td>{new Date(alert.created_at).toLocaleString("en-GB")}</td>
                                <td className="text-end">
                                    <Button variant="outline-dark" size="sm" onClick={() => handleClear(alert.id)}>
                                        Clear
                                    </Button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            )}
        </Alert>
    );
};

export default PriceAlertsBanner;